import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Mic, Volume2, VolumeX, Sparkles, Hand, ShieldCheck, BookOpen, Lightbulb, Languages } from "lucide-react";
import { AskTrainer } from "@/components/AskTrainer";
import { LiveZoya } from "@/components/LiveZoya";
import { LangPicker } from "@/components/smart/ClassControls";
import { hi } from "@/lib/classroom_hi";
import { useLang } from "@/context/LanguageContext";
import { askZoya, zoyaSession, useSpeechInput, clip } from "@/lib/zoya";
import { isRepeat, langName, useClassLang, useClassPrefs } from "@/lib/smart";

const MSGS = "rs_zoya_msgs";
const PAUSE_MS = 40000; // a long silence after an answer or a raised hand = maybe stuck
const loadMsgs = () => { try { return JSON.parse(localStorage.getItem(MSGS) || "[]"); } catch { return []; } };
const SUGGEST = [
  ["simple", "Explain this lesson in simple words", "ये पाठ आसान शब्दों में समझाइए"],
  ["example", "Give me a real-life example", "इसका एक असली ज़िंदगी का उदाहरण दीजिए"],
  ["project", "Which small project can I build at home?", "घर पर कौन सा छोटा प्रोजेक्ट बना सकता हूँ?"],
  ["earn", "How do I start earning with Learn & Earn?", "लर्न एंड अर्न से कमाई कैसे शुरू करूँ?"],
];
const ROLES = [["student", "Student", "छात्र"], ["teacher", "Teacher", "टीचर"], ["parent", "Parent", "अभिभावक"], ["coordinator", "Coordinator", "कोऑर्डिनेटर"]];
const TOPIC = { robotics: ["Robotics", "रोबोटिक्स"], drones: ["Drones", "ड्रोन"], ai: ["AI", "AI"], electronics_coding: ["Electronics & coding", "इलेक्ट्रॉनिक्स और कोडिंग"],
  robospark: ["RoboSpark", "RoboSpark"], other_study: ["Science", "विज्ञान"], off_topic: ["Off-topic", "विषय से बाहर"] };

const GuardTags = ({ g, i, trainer, doubt }) => {
  const { L } = useLang();
  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5 text-[10px] font-semibold" data-testid={`ask-zoya-guard-${i}`}>
      {g?.checked && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-400/10 text-emerald-700" data-testid={`ask-zoya-guard-safe-${i}`}><ShieldCheck className="w-3 h-3" />{g.blocked ? L("Redirected by safety check", "सुरक्षा जाँच ने मोड़ा") : L("Safety checked", "सुरक्षा जाँच हुई")}{g.topic && !g.blocked ? ` · ${L(...(TOPIC[g.topic] || [g.topic, g.topic]))}` : ""}</span>}
      {g?.chapter && !g.blocked && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#00E5FF]/15 text-[#00707F]" data-testid={`ask-zoya-guard-chapter-${i}`}><BookOpen className="w-3 h-3" />{L(`Lesson ${g.lesson_n} · ${g.chapter}`, `पाठ ${g.lesson_n} · ${hi(g.chapter)}`)}</span>}
      {doubt && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FFF3D6] text-[#7A4A00]" data-testid={`ask-zoya-doubt-tag-${i}`}><Lightbulb className="w-3 h-3" />{L("Explained more simply", "और आसान करके समझाया")}</span>}
      {trainer}
    </div>
  );
};

const ZoyaCard = ({ teacher, speaking, levelRef, lesson }) => {
  const { L } = useLang();
  return (
    <div className="rounded-3xl overflow-hidden border border-slate-200 bg-[#E8F0FE] relative self-start" data-testid="ask-zoya-avatar">
      <LiveZoya src={teacher} speaking={speaking} levelRef={levelRef} portrait={false} testid="ask-zoya-live" />
      <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-white via-white/85 to-transparent text-[#0A192F]">
        <div className="flex items-center gap-2 font-extrabold text-lg">Zoya Ma'am
          <span className={`w-2.5 h-2.5 rounded-full ${speaking ? "bg-[#FF2E63] animate-ping" : "bg-emerald-500"}`} />
        </div>
        <div className="text-xs font-semibold text-[#007A8A]" data-testid="ask-zoya-status">
          {speaking ? L("Speaking…", "बोल रही हैं…") : L("AI humanoid teacher · online", "AI ह्यूमनॉइड टीचर · ऑनलाइन")}
        </div>
        <div className="mt-1 text-[11px] text-slate-600 line-clamp-1">{L("Now teaching:", "अभी पढ़ा रही हैं:")} {lesson}</div>
      </div>
      {speaking && <div className="absolute top-3 right-3 flex items-end gap-1 h-6">{[0, 1, 2, 3].map((i) => <span key={i} className="w-1.5 rounded-full bg-[#00B8D4] animate-bounce" style={{ height: `${10 + i * 4}px`, animationDelay: `${i * 0.12}s` }} />)}</div>}
    </div>
  );
};

const Bubble = ({ m, i, trainer }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ask-zoya-msg-${i}`}>
    <div className="max-w-[85%]">
      <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#FF3B3B] text-white rounded-br-md" : "bg-white text-slate-800 rounded-bl-md border border-slate-200"}`}>
        {m.text || <span className="inline-flex gap-1">{[0, 1, 2].map((k) => <span key={k} className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: `${k * 0.15}s` }} />)}</span>}
      </div>
      {m.role === "zoya" && (m.guard || trainer || m.doubt) && <GuardTags g={m.guard} i={i} trainer={trainer} doubt={m.doubt} />}
    </div>
  </div>
);

const RoleChips = () => {
  const { L } = useLang();
  const { role, setRole } = useClassPrefs();
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="ask-zoya-roles">
      <span className="text-[11px] font-semibold text-slate-500 mr-1">{L("I am a", "मैं हूँ")}</span>
      {ROLES.map(([r, en, h]) => (
        <button key={r} type="button" onClick={() => setRole(r)} data-testid={`ask-zoya-role-${r}`} aria-pressed={role === r}
          className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-colors ${role === r ? "bg-[#0A192F] border-[#0A192F] text-white" : "border-slate-300 text-slate-600 hover:border-[#00B8D4]"}`}>{L(en, h)}</button>
      ))}
    </div>
  );
};

const DoubtNudge = ({ onSimple, onDismiss }) => {
  const { L } = useLang();
  return (
    <div className="flex justify-start" data-testid="ask-zoya-doubt-nudge">
      <div className="max-w-[85%] px-4 py-3 rounded-2xl rounded-bl-md bg-[#FFF8E6] border border-[#FFC94D] text-sm text-[#0A192F]">
        <div className="font-semibold">{L("Thinking it over? Shall I explain that again in an easier way?", "सोच रहे हैं? क्या मैं इसे और आसान तरीके से दोबारा समझाऊँ?")}</div>
        <div className="mt-2 flex flex-wrap gap-2">
          <button onClick={onSimple} data-testid="ask-zoya-doubt-yes" className="px-3 py-1.5 rounded-full bg-[#0A192F] text-white text-xs font-bold">{L("Yes, explain simply", "हाँ, आसान करके समझाइए")}</button>
          <button onClick={onDismiss} data-testid="ask-zoya-doubt-no" className="px-3 py-1.5 rounded-full border border-slate-300 text-xs font-bold">{L("I'm fine", "समझ गया")}</button>
        </div>
      </div>
    </div>
  );
};

export const AskZoya = ({ lesson, context, audio, onBusy, name = "", level = "intermediate" }) => {
  const { L } = useLang();
  const lang = useClassLang();
  const { role, setLang } = useClassPrefs();
  const [msgs, setMsgs] = useState(loadMsgs);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const [nudge, setNudge] = useState(false);
  const box = useRef(null);
  const inputRef = useRef(null);
  const root = useRef(null);
  const timer = useRef(null);
  const mic = useSpeechInput(lang, (t) => setInput(t));

  useEffect(() => { localStorage.setItem(MSGS, JSON.stringify(msgs.slice(-20))); box.current?.scrollTo({ top: 1e6, behavior: "smooth" }); }, [msgs, nudge]);
  const armPause = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const r = root.current?.getBoundingClientRect();
      if (document.visibilityState === "visible" && r && r.top < window.innerHeight && r.bottom > 0) setNudge(true);
    }, PAUSE_MS);
  };
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => {
    if (!context?.at) return;
    root.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    inputRef.current?.focus({ preventScroll: true });
    if (voiceOn) audio.play(clip("hand"));
    armPause();
  }, [context?.at]); // eslint-disable-line react-hooks/exhaustive-deps

  const setLast = (patch) => setMsgs((m) => [...m.slice(0, -1), { ...m[m.length - 1], role: "zoya", ...patch }]);
  const ask = async (q, intent = "", forceDoubt = false) => {
    const question = q.trim();
    if (!question || busy) return;
    const recent = msgs.filter((m) => m.role === "user").slice(-3).map((m) => m.text);
    const doubt = forceDoubt || (!intent && isRepeat(question, recent));
    clearTimeout(timer.current);
    setNudge(false);
    setInput("");
    setBusy(true);
    onBusy?.();
    audio.stop();
    setMsgs((m) => [...m, { role: "user", text: question }, { role: "zoya", text: "", doubt }]);
    let replyLang = lang;
    try {
      const full = await askZoya({ session_id: zoyaSession(), question, lesson: lesson.title, chapter: context?.chapter || "", lang, intent,
        student_name: name, role, level, doubt }, (text) => setLast({ text }), (guard, meta) => {
        setLast({ guard });
        if (meta.switched && meta.lang !== lang) { replyLang = meta.lang; setLang(meta.lang); toast.success(L(`Zoya Ma'am now speaks ${langName(meta.lang)}`, `ज़ोया मैम अब ${langName(meta.lang)} में बोलेंगी`)); }
      }, (suggest) => setLast({ suggest }));
      if (voiceOn && full) audio.say(full, replyLang);
      armPause();
    } catch (e) {
      setLast({ text: e.message === "busy" ? L("Many students are asking right now. Please try again in a minute.", "अभी बहुत सारे छात्र पूछ रहे हैं। एक मिनट बाद फिर पूछिए।")
        : L("Sorry, I couldn't answer right now. Please try again.", "माफ़ कीजिए, अभी जवाब नहीं दे पाई। कृपया फिर से पूछिए।") });
    } finally { setBusy(false); }
  };
  const lastQ = [...msgs].reverse().find((m) => m.role === "user")?.text;
  const followUps = !busy && msgs[msgs.length - 1]?.role === "zoya" ? msgs[msgs.length - 1].suggest || [] : [];
  const explainSimply = () => ask(lastQ || L("Explain this lesson in simple words", "ये पाठ आसान शब्दों में समझाइए"), lastQ ? "" : "simple", true);

  return (
    <section ref={root} className="scroll-mt-24 grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6" data-testid="ask-zoya">
      <ZoyaCard teacher={lesson.teacher || "/classroom/zoya.jpg"} speaking={audio.speaking} levelRef={audio.levelRef} lesson={L(lesson.title, lesson.title_hi)} />
      <div className="rounded-3xl border border-slate-200 bg-[#E8F0FE] p-4 sm:p-5 flex flex-col min-h-[420px]">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 font-extrabold text-lg"><Sparkles className="w-5 h-5 text-[#FF2E63]" />{L("Ask Zoya Ma'am", "ज़ोया मैम से पूछिए")}</div>
            <div className="text-xs text-slate-500">{context?.chapter ? <span className="inline-flex items-center gap-1 text-[#B45309]" data-testid="ask-zoya-context"><Hand className="w-3 h-3" />{L("Hand raised at:", "इस हिस्से पर हाथ उठाया:")} {context.chapter}</span>
              : <span className="inline-flex items-center gap-1"><Languages className="w-3 h-3" />{L(`Type or speak your doubt. She answers in ${langName(lang)} with her voice. Say "Tamil mein batao" to switch.`, `सवाल लिखिए या बोलिए, वे ${langName(lang)} में आवाज़ के साथ जवाब देंगी। भाषा बदलने के लिए "Tamil mein batao" कहिए।`)}</span>}</div>
          </div>
          <button onClick={() => { setVoiceOn(!voiceOn); audio.stop(); }} data-testid="ask-zoya-voice-toggle" aria-label="Voice replies"
            className={`w-10 h-10 shrink-0 rounded-full grid place-items-center border ${voiceOn ? "bg-[#00E5FF]/20 border-[#00B8D4] text-[#00707F]" : "border-slate-300 text-slate-500"}`}>
            {voiceOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>
        </div>
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between">
          <RoleChips />
          <LangPicker compact testid="ask-zoya-lang" />
        </div>
        <div ref={box} className="mt-4 flex-1 overflow-y-auto space-y-3 max-h-[360px] pr-1" data-testid="ask-zoya-messages">
          <Bubble m={{ role: "zoya", text: L(`Namaste${name ? ` ${name}` : ""}! I'm Zoya Ma'am. Ask me anything about this lesson, robots, drones or AI.`, `नमस्ते${name ? ` ${name}` : ""}! मैं ज़ोया मैम हूँ। इस पाठ, रोबोट, ड्रोन या AI के बारे में कुछ भी पूछिए।`) }} i="greet" />
          {msgs.map((m, i) => <Bubble key={i} m={m} i={i} trainer={m.role === "zoya" && m.text && msgs[i - 1]?.role === "user" && !(busy && i === msgs.length - 1)
            ? <AskTrainer i={i} question={msgs[i - 1].text} answer={m.text} lesson={lesson.title} chapter={context?.chapter || ""} flagged={Boolean(m.guard?.needs_trainer)} /> : null} />)}
          {nudge && !busy && <DoubtNudge onSimple={explainSimply} onDismiss={() => setNudge(false)} />}
        </div>
        {followUps.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="ask-zoya-followups">
            {followUps.map((q, i) => <button key={i} onClick={() => ask(q)} data-testid={`ask-zoya-followup-${i}`} className="px-3 py-2 sm:py-1.5 rounded-full text-xs font-semibold bg-[#0A192F] text-white hover:bg-[#00B8D4] hover:text-[#0A192F] transition-colors">{q}</button>)}
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          {SUGGEST.map(([intent, en, hiT], i) => <button key={i} disabled={busy} onClick={() => ask(L(en, hiT), intent)} data-testid={`ask-zoya-suggest-${i}`} className="px-3 py-2 sm:py-1.5 rounded-full text-xs border border-slate-300 text-slate-600 hover:border-[#00E5FF] hover:text-[#0A192F] transition-colors">{L(en, hiT)}</button>)}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); ask(input); }} className="mt-3 flex items-center gap-2">
          <input ref={inputRef} value={input} onChange={(e) => { setInput(e.target.value); clearTimeout(timer.current); }} maxLength={400} data-testid="ask-zoya-input"
            placeholder={L("Type your question…", "अपना सवाल लिखिए…")} className="flex-1 min-w-0 h-12 px-4 rounded-full bg-[#F0F4F8] border border-slate-300 focus:border-[#00E5FF] outline-none text-sm" />
          {mic.supported && <button type="button" onClick={mic.start} data-testid="ask-zoya-mic-btn" aria-label="Speak"
            className={`w-12 h-12 shrink-0 rounded-full grid place-items-center border ${mic.listening ? "bg-[#FF2E63] border-[#FF2E63] animate-pulse" : "border-slate-300 hover:bg-slate-900/5"}`}><Mic className="w-5 h-5" /></button>}
          <button type="submit" disabled={busy || !input.trim()} data-testid="ask-zoya-send-btn" aria-label="Send" onMouseDown={(e) => e.preventDefault()}
            className="w-12 h-12 shrink-0 rounded-full grid place-items-center bg-[#FF3B3B] hover:bg-[#E63946] disabled:opacity-40"><Send className="w-5 h-5" /></button>
        </form>
      </div>
    </section>
  );
};
